import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { cn } from "../lib/utils";

type DialogSize = "sm" | "md" | "lg";

interface DialogProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  children?: React.ReactNode;
  className?: string;
  size?: DialogSize;
  showClose?: boolean;
}

export function Dialog({ open, onClose, title, description, children, className, size, showClose = true }: DialogProps) {
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      ref={overlayRef}
      className="dialog-overlay"
      onMouseDown={(e) => { if (e.target === overlayRef.current) onClose(); }}
      aria-modal="true"
      role="dialog"
      aria-labelledby={title ? "dialog-title" : undefined}
      aria-describedby={description ? "dialog-desc" : undefined}
    >
      <div className={cn("dialog", size && `dialog-${size}`, className)}>
        <div className="dialog-header">
          <div className="dialog-header-text">
            {title && <h2 id="dialog-title" className="dialog-title">{title}</h2>}
            {description && <p id="dialog-desc" className="dialog-description">{description}</p>}
          </div>
          {showClose && (
            <button className="dialog-close" onClick={onClose} aria-label="Close dialog">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}
        </div>
        {children && <div className="dialog-content">{children}</div>}
      </div>
    </div>,
    document.body
  );
}
